import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLazyQuery } from '@apollo/client';
import { Card, CardContent, Loading, Pagination, Select } from '@guildeducationinc/recess';
import { Button } from '@mui/material';
import { DOWNLOAD_REPORT, GET_REPORT_COLUMNS, TEST_REPORT_TEMPLATE, } from '@src/graphql/queries/reportTemplate';
import _ from 'lodash';
import { MessageType, Messages, classNames, useLocale } from 'pe-core-frontend';
import { useEffect, useState } from 'react';
export const ReportViewer = ({ reportTemplate }) => {
    const { t } = useLocale();
    const [messages, setMessages] = useState([]);
    const [downloadReport] = useLazyQuery(DOWNLOAD_REPORT);
    const [testReportTemplate] = useLazyQuery(TEST_REPORT_TEMPLATE);
    const [getReportColumns] = useLazyQuery(GET_REPORT_COLUMNS);
    const [processing, setProcessing] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [totalPages, setTotalPages] = useState(0);
    const [reportColumnLookup, setReportColumnLookup] = useState({});
    useEffect(() => {
        async function fetchData() {
            try {
                setProcessing(true);
                const input = {
                    ..._.omit(reportTemplate, [
                        'id',
                        'createdAt',
                        'updatedAt',
                        '__typename',
                        'lastRunAt',
                        'lastRunStatus',
                        'lastRunMessage',
                    ]),
                    id: reportTemplate.id ? reportTemplate.id : null,
                    integrationConfigId: reportTemplate.integrationConfigId
                        ? reportTemplate.integrationConfigId
                        : null,
                    cronSchedules: reportTemplate.cronSchedules ?? null,
                    startDate: reportTemplate.startDate ?? null,
                    daysApart: reportTemplate.daysApart ? reportTemplate.daysApart : null,
                    type: reportTemplate.type,
                };
                const columns = await getReportColumns({
                    variables: {
                        input,
                    },
                });
                setReportColumnLookup(columns.data.getReportColumns.reduce((acc, col) => {
                    acc[col.code] = col;
                    return acc;
                }, {}));
                const results = await testReportTemplate({
                    variables: {
                        input,
                        pagination: {
                            page,
                            pageSize,
                        },
                    },
                });
                setReportData(results.data.testReportTemplate.items);
                setTotalPages(results.data.testReportTemplate.total / pageSize);
            }
            catch {
                setMessages([{ type: MessageType.ERROR, value: t('Error loading report') }]);
            }
            finally {
                setProcessing(false);
            }
        }
        fetchData();
    }, [reportTemplate, page, pageSize]);
    const handleDownloadReport = async () => {
        const response = await downloadReport({
            variables: {
                input: {
                    ..._.omit(reportTemplate, [
                        'id',
                        'createdAt',
                        'updatedAt',
                        '__typename',
                        'lastRunAt',
                        'lastRunStatus',
                        'lastRunMessage',
                    ]),
                    id: reportTemplate.id ? reportTemplate.id : null,
                    integrationConfigId: reportTemplate.integrationConfigId
                        ? reportTemplate.integrationConfigId
                        : null,
                    cronSchedules: reportTemplate.cronSchedules ?? null,
                    startDate: reportTemplate.startDate ?? null,
                    daysApart: reportTemplate.daysApart ? reportTemplate.daysApart : null,
                    type: reportTemplate.type,
                },
            },
        });
        if (response.data?.downloadReport) {
            window.open(response.data?.downloadReport);
        }
        else {
            setMessages([{ type: MessageType.ERROR, value: t('Failed to download report') }]);
        }
    };
    return (_jsxs("div", { children: [_jsx(Messages, { messages: messages, onClose: () => setMessages([]) }), _jsx(Loading, { label: 'Report Template', loaded: !processing, showChildrenWhileLoading: true, overlay: true, children: _jsx(Card, { children: _jsx(CardContent, { children: _jsxs("div", { className: 'flex flex-col', children: [_jsxs("div", { className: 'flex items-end justify-between', children: [_jsxs("div", { children: [_jsx("div", { className: 'text-3xl', children: reportTemplate.name || t('Report Data') }), reportTemplate.type && (_jsx("div", { className: 'text-xl', children: t(`reportType.${reportTemplate.type}`) }))] }), reportData.length > 0 && (_jsx(Button, { variant: 'contained', onClick: handleDownloadReport, children: t('Download Report') }))] }), messages.length > 0 && _jsx(Messages, { messages: messages }), reportData.length > 0 && (_jsxs("div", { children: [_jsx(ReportViewerPagination, { page: page, pageSize: pageSize, totalPages: totalPages, onPageChange: page => setPage(page), onPageSizeChange: pageSize => setPageSize(pageSize) }), _jsx("div", { className: 'overflow-x-auto text-nowrap', children: _jsxs("table", { className: 'min-w-full divide-y divide-gray-900 border border-1 border-gray-900 my-3', children: [_jsx("thead", { children: _jsx("tr", { children: Object.keys(reportData[0]).map(key => (_jsx("th", { className: classNames('p-2 text-left', 'text-md'), children: reportColumnLookup[key]?.label || key }, key))) }) }), _jsx("tbody", { className: 'divide-y divide-gray-900', children: reportData.map((row, idx) => (_jsx("tr", { children: Object.values(row).map((val, idx) => (_jsx("td", { className: 'p-2', children: val }, idx))) }, idx))) })] }) }), _jsx(ReportViewerPagination, { page: page, pageSize: pageSize, totalPages: totalPages, onPageChange: page => setPage(page), onPageSizeChange: pageSize => setPageSize(pageSize) })] })), reportData.length === 0 && _jsx("div", { children: t('No data found') })] }) }) }) })] }));
};
const ReportViewerPagination = ({ page, pageSize, totalPages, onPageSizeChange, onPageChange, }) => {
    return (_jsxs("div", { className: 'flex justify-between', children: [totalPages > 1 && (_jsx(Pagination, { isSlim: false, currentPage: page, onChangePage: page => onPageChange(page), totalPages: totalPages })), totalPages <= 1 && _jsx("div", {}), _jsx(Select, { label: '', value: pageSize, onChange: e => {
                    onPageSizeChange(Number(e.target.value ? e.target.value : 15));
                }, className: 'w-40', options: [
                    { value: 5, label: '5' },
                    { value: 15, label: '15' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                ] })] }));
};
